import { Copy, Download, ExternalLink, FileText, MessageCircle, Share2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { apiBlob } from "../api";
import { useAuth } from "../auth";
import { PageBackLink, StateView } from "../components";
import { brl, useApiData } from "../hooks";
import { brazilianPhoneToE164, formatBrazilianPhone } from "../input-masks";
import {
  buildFiscalShareMessage,
  buildWhatsAppUrl,
  downloadBlob,
  sharePdf,
  sharePdfFileOnly,
  supportsFileShare,
  supportsWindowsAssistedShare,
} from "../quote-sharing";
import type { CustomerDetail, FiscalDocument, WorkOrder } from "../types";

export function FiscalSharePage() {
  const { workOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedDocumentId = searchParams.get("documentId");
  const { user } = useAuth();
  const canDownload = user?.permissions.includes("fiscal.download") ?? false;
  const workOrder = useApiData<WorkOrder>(workOrderId ? `/work-orders/${workOrderId}` : null);
  const fiscalDocuments = useApiData<FiscalDocument[]>(
    workOrderId && canDownload ? `/fiscal/documents?workOrderId=${workOrderId}&limit=100` : null,
  );
  const fiscalDocument = requestedDocumentId
    ? fiscalDocuments.data?.find(({ id }) => id === requestedDocumentId) ?? null
    : fiscalDocuments.data?.find(({ status, documents }) => status === "authorized" && documents.pdf) ?? null;
  const customer = useApiData<CustomerDetail>(
    workOrder.data?.customerId && fiscalDocument?.status === "authorized"
      ? `/customers/${workOrder.data.customerId}`
      : null,
  );
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [busy, setBusy] = useState<"load" | "share" | "copy" | "download" | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const primaryContact = useMemo(
    () => customer.data?.contacts.find(({ isPrimary, active }) => isPrimary && active),
    [customer.data],
  );
  const customerName = workOrder.data?.customerName ?? customer.data?.legalName ?? "Cliente";
  const fileName = fiscalDocument ? `nfse-${fiscalDocument.nfseNumber ?? fiscalDocument.id}.pdf` : "nfse.pdf";
  const phone = brazilianPhoneToE164(recipientPhone);
  const whatsappUrl = phone ? buildWhatsAppUrl(phone, messageText) : "#";
  const nativeShare = supportsFileShare();
  const windowsShare = supportsWindowsAssistedShare();

  useEffect(() => {
    if (!workOrder.data || !fiscalDocument) return;
    setMessageText(buildFiscalShareMessage({
      customerName,
      nfseNumber: fiscalDocument.nfseNumber,
      workOrderNumber: workOrder.data.number,
      sandbox: fiscalDocument.environment === "sandbox",
    }));
  }, [customerName, fiscalDocument?.id, fiscalDocument?.nfseNumber, fiscalDocument?.environment, workOrder.data?.id, workOrder.data?.number]);

  useEffect(() => {
    if (!customer.data) return;
    setRecipientPhone(formatBrazilianPhone(
      customer.data.whatsapp
      ?? primaryContact?.whatsapp
      ?? customer.data.phone
      ?? primaryContact?.phone
      ?? "",
    ));
  }, [customer.data, primaryContact]);

  useEffect(() => {
    if (!fiscalDocument?.documents.pdf) return;
    let active = true;
    setBusy("load");
    setError("");
    void apiBlob(`/fiscal/documents/${fiscalDocument.id}/file/pdf`)
      .then((blob) => { if (active) setPdfBlob(blob); })
      .catch((caught) => { if (active) setError(caught instanceof Error ? caught.message : "Não foi possível carregar o PDF."); })
      .finally(() => { if (active) setBusy(null); });
    return () => { active = false; };
  }, [fiscalDocument?.id, fiscalDocument?.documents.pdf]);

  useEffect(() => {
    if (!pdfBlob) { setPdfObjectUrl(null); return; }
    const objectUrl = URL.createObjectURL(pdfBlob);
    setPdfObjectUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pdfBlob]);

  const copyMessage = async () => {
    setBusy("copy"); setError(""); setNotice("");
    try {
      await navigator.clipboard.writeText(messageText);
      setNotice("Mensagem copiada.");
    } catch {
      setError("Não foi possível copiar a mensagem.");
    } finally { setBusy(null); }
  };

  const shareDocument = async () => {
    if (!pdfBlob || !workOrder.data || !fiscalDocument) return;
    setBusy("share"); setError(""); setNotice("");
    try {
      if (nativeShare) {
        const result = await sharePdf({
          blob: pdfBlob,
          fileName,
          message: messageText,
          title: `NFS-e ${fiscalDocument.nfseNumber ?? workOrder.data.number}`,
        });
        setNotice(result.messageCopied
          ? "PDF compartilhado e mensagem copiada."
          : "PDF compartilhado.");
        return;
      }
      if (windowsShare) {
        await Promise.all([
          navigator.clipboard.writeText(messageText),
          sharePdfFileOnly({
            blob: pdfBlob,
            fileName,
            title: `NFS-e ${fiscalDocument.nfseNumber ?? workOrder.data.number}`,
          }),
        ]);
        setNotice("PDF compartilhado. A mensagem está pronta para colar.");
      }
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : "Não foi possível compartilhar a NFS-e.");
    } finally { setBusy(null); }
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    setBusy("download"); setError(""); setNotice("");
    try {
      downloadBlob(pdfBlob, fileName);
      setNotice("PDF baixado.");
    } finally { setBusy(null); }
  };

  if (!canDownload || (!fiscalDocuments.loading && (!fiscalDocument || fiscalDocument.status !== "authorized" || !fiscalDocument.documents.pdf))) {
    return <Navigate to={workOrderId ? `/ordens/${workOrderId}` : "/ordens"} replace />;
  }

  const loading = workOrder.loading || fiscalDocuments.loading || customer.loading;
  const loadError = workOrder.error || fiscalDocuments.error || customer.error;

  return <StateView loading={loading} error={loadError}>
    {workOrder.data && fiscalDocument && <section className="app-section compact fiscal-share-page">
      <div className="app-container">
        <PageBackLink to={`/ordens/${workOrder.data.id}`} context="Nota fiscal" />
        <article className="workspace-card quote-send-preview fiscal-send-preview">
          <header className="quote-send-header">
            <div><h1>Enviar nota fiscal</h1></div>
          </header>
          <div className="quote-send-summary fiscal-send-summary">
            <span>Cliente<strong>{customerName}</strong></span>
            <span>NFS-e<strong>{fiscalDocument.nfseNumber ?? "Autorizada"}</strong></span>
            <span>OS<strong>{workOrder.data.number}</strong></span>
            <span>Valor<strong>{brl(fiscalDocument.serviceAmount)}</strong></span>
          </div>
          <div className="quote-send-fields">
            <label>Destinatário<input value={primaryContact?.name ?? customerName} readOnly /></label>
            <label>WhatsApp<input inputMode="tel" value={recipientPhone} onChange={(event) => setRecipientPhone(formatBrazilianPhone(event.target.value))} /></label>
            <label className="quote-send-message">Mensagem<textarea rows={6} value={messageText} onChange={(event) => setMessageText(event.target.value)} maxLength={4096} /></label>
          </div>
          <section className="quote-pdf-preview">
            <header>
              <div><FileText size={18} /><span><strong>{fileName}</strong><small>{pdfBlob ? `${Math.max(1, Math.round(pdfBlob.size / 1024))} KB · PDF` : "PDF"}</small></span></div>
              <a href={pdfObjectUrl ?? undefined} target="_blank" rel="noreferrer" aria-disabled={!pdfObjectUrl} onClick={(event) => { if (!pdfObjectUrl) event.preventDefault(); }}><ExternalLink size={15} />Abrir PDF</a>
            </header>
            {pdfObjectUrl
              ? <iframe src={pdfObjectUrl} title={`Pré-visualização da NFS-e ${fiscalDocument.nfseNumber ?? ""}`} />
              : <div className="quote-pdf-loading" role="status">{busy === "load" ? "Carregando PDF…" : "PDF indisponível"}</div>}
          </section>
          <div className="quote-share-actions fiscal-share-actions">
            <button type="button" className="site-button site-button-outline" onClick={() => void copyMessage()} disabled={busy !== null}><Copy size={16} />{busy === "copy" ? "Copiando…" : "Copiar mensagem"}</button>
            {(nativeShare || windowsShare) && <button type="button" className="site-button site-button-red" onClick={() => void shareDocument()} disabled={busy !== null || !pdfBlob}><Share2 size={16} />{busy === "share" ? "Compartilhando…" : "Compartilhar PDF"}</button>}
            {!nativeShare && !windowsShare && <>
              <a className={`site-button site-button-red${!phone ? " disabled" : ""}`} href={whatsappUrl} target="_blank" rel="noreferrer" onClick={(event) => { if (!phone) { event.preventDefault(); setError("Confira o número de WhatsApp."); } else void copyMessage(); }}><MessageCircle size={16} />Abrir WhatsApp</a>
              <button type="button" className="site-button site-button-outline quote-download-action" onClick={downloadPdf} disabled={busy !== null || !pdfBlob}><Download size={16} />{busy === "download" ? "Baixando…" : "Baixar PDF"}</button>
            </>}
          </div>
          {notice && <div className="operation-notice" role="status">{notice}</div>}
          {error && <div className="form-error">{error}</div>}
        </article>
      </div>
    </section>}
  </StateView>;
}
